import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChatService } from './chat.service';
import {ApiService} from "../../../api/api.service";

interface ChatMessage {
  text: string;
  isUser: boolean;
  timestamp: Date;
}

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatComponent implements OnInit, AfterViewChecked {
  @ViewChild('chatMessages') private chatMessagesContainer: ElementRef;
  chatForm: FormGroup;
  messages: ChatMessage[] = [];
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$: Observable<boolean>;
  private shouldScrollToBottom = false;
  private sessionId: string = '';

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private chatService: ChatService,
    private api: ApiService
  ) {
    this.loading$ = this.loadingSubject.asObservable();
  }

  ngOnInit(): void {
    this.chatForm = this.fb.group({
      message: ['', Validators.required]
    });

    // Add initial welcome message from AI
    this.addMessage('Hello! I am your AI assistant. How can I help you today?', false);
  }

  sendMessage(): void {
    if (this.chatForm.invalid) {
      return;
    }

    const userMessage = this.chatForm.get('message').value;
    this.addMessage(userMessage, true);
    this.chatForm.reset();

    // Show loading indicator
    this.loadingSubject.next(true);

    this.api.getChatResponse({message: userMessage, sessionId: this.sessionId, module: 'knowledgebase'}).subscribe( res => {
      this.sessionId = res.sessionId;
      this.addMessage(res.response, false);
      this.loadingSubject.next(false);
    })
  }

  private addMessage(text: string, isUser: boolean): void {
    this.messages.push({
      text,
      isUser,
      timestamp: new Date()
    });
    this.shouldScrollToBottom = true;
    this.cdr.detectChanges();
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private scrollToBottom(): void {
    try {
      const element = this.chatMessagesContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  private getAIResponse(userMessage: string): void {
    // Use the chat service to get the AI response
    this.chatService.getAIResponse(userMessage).subscribe(
      (response) => {
        // Hide loading indicator and add AI response
        this.loadingSubject.next(false);
        this.addMessage(response, false);
      },
      (error) => {
        console.error('Error getting AI response:', error);
        this.loadingSubject.next(false);
        this.addMessage('Sorry, I encountered an error. Please try again later.', false);
      }
    );
  }
}
